import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import MarkedReact from "marked-react";
import React, { useEffect, useMemo, useState } from "react";
import { BoardAiApi, BoardAiMarkdownResult } from "../../../services/board-ai-api";
import { BoardApi } from "../../../services/board-service-api";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import AiSparkleIcon from "./ai-sparkle-icon";
import BoardDatePicker from "./board-date-picker";
import { boardKeys } from "./board-query-keys";
import "./board-releases.scss";
import { BoardService } from "./board-service";
import { roadmapChanged } from "./change-event-routing";
import ConfirmDialog from "./confirm-dialog";
import { SAFE_MARKDOWN_RENDERER } from "./safe-markdown";
import { BoardChangeEvent, BoardConfig, Version, VersionProgress } from "./types";
import { activeBoardScope, useBoardConfig } from "./use-board";
import { useBoardAiEnabled } from "./use-board-ai";

interface ProjectReleasesProps {
  projectKey: string;
}

type ReleaseStatus = "released" | "overdue" | "unreleased";

interface ReleaseProgress {
  total: number;
  done: number;
}

const NO_PROGRESS: ReleaseProgress = { total: 0, done: 0 };

const STATUS_LABEL: Record<ReleaseStatus, string> = {
  released: "Released",
  overdue: "Overdue",
  unreleased: "Unreleased"
};

// A release is overdue when it has a past due date and hasn't shipped — the one state that earns a
// warning color, mirroring how the backlog flags an over-capacity sprint.
function statusOf(version: Version): ReleaseStatus {
  if (version.released) return "released";
  const due: string | null = version.releaseDate;
  const isOverdue: boolean = due !== null && dayjs(due).isBefore(dayjs(), "day");
  return isOverdue ? "overdue" : "unreleased";
}

interface ReleaseRowProps {
  version: Version;
  progress: ReleaseProgress;
  canManage: boolean;
  // AI release-notes generation is offered per release when the workspace has AI enabled.
  aiEnabled: boolean;
  projectKey: string;
  onRequestDelete: (version: Version) => void;
  onOptimisticUpdate: (version: Version) => void;
}

// One release. Owners get inline editing (name on blur, date picker, release toggle, delete);
// everyone else gets a read-only card. All edits commit a single complete value — no per-keystroke
// server writes (the churn that previously made fields feel broken) — and each edit patches the
// shared config cache immediately so the badge/group/date update without waiting on the refetch.
const ReleaseRow: React.FC<ReleaseRowProps> = ({
  version,
  progress,
  canManage,
  aiEnabled,
  projectKey,
  onRequestDelete,
  onOptimisticUpdate
}) => {
  const [name, setName] = useState<string>(version.name);
  const [description, setDescription] = useState<string>(version.description ?? "");
  const [generating, setGenerating] = useState<boolean>(false);
  useEffect(() => {
    setName(version.name);
  }, [version.name]);
  useEffect(() => {
    setDescription(version.description ?? "");
  }, [version.description]);

  const status: ReleaseStatus = statusOf(version);
  const pct: number = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const dateText: string | null = version.releaseDate ? dayjs(version.releaseDate).format("MMM D, YYYY") : null;

  let dateLine: string;
  if (status === "released") dateLine = dateText ? `Released ${dateText}` : "Released";
  else if (status === "overdue") dateLine = `Overdue · was due ${dateText ?? ""}`.trim();
  else if (dateText) dateLine = `Due ${dateText}`;
  else dateLine = "No release date";

  const commitName = (): void => {
    const trimmed: string = name.trim();
    if (trimmed.length === 0 || trimmed === version.name) {
      setName(version.name);
      return;
    }
    onOptimisticUpdate({ ...version, name: trimmed });
    void BoardService.updateVersion(version.id, trimmed, version.releaseDate, version.released, version.description);
  };
  const setReleaseDate = (next: string | null): void => {
    onOptimisticUpdate({ ...version, releaseDate: next });
    void BoardService.updateVersion(version.id, version.name, next, version.released, version.description);
  };
  const toggleReleased = (): void => {
    const next: boolean = !version.released;
    onOptimisticUpdate({ ...version, released: next });
    void BoardService.updateVersion(version.id, version.name, version.releaseDate, next, version.description);
  };
  const commitDescription = (): void => {
    const trimmed: string = description.trim();
    const next: string | null = trimmed.length === 0 ? null : trimmed;
    if (next === (version.description ?? null)) {
      setDescription(version.description ?? "");
      return;
    }
    onOptimisticUpdate({ ...version, description: next });
    void BoardService.updateVersion(version.id, version.name, version.releaseDate, version.released, next);
  };

  // Draft this release's notes from its completed (done) issues and write them into the notes field.
  // Scoped to this version so the model only sees a bounded, relevant set — not the whole board.
  const generateNotes = async (): Promise<void> => {
    if (generating) return;
    setGenerating(true);
    try {
      const result: BoardAiMarkdownResult = await BoardAiApi.releaseNotes({ versionId: version.id, projectKey });
      const markdown: string = (result.markdown ?? "").trim();
      if (markdown.length === 0) return;
      setDescription(markdown);
      onOptimisticUpdate({ ...version, description: markdown });
      void BoardService.updateVersion(version.id, version.name, version.releaseDate, version.released, markdown);
    } catch {
      // surfaced by the api layer
    } finally {
      setGenerating(false);
    }
  };

  // Grow the editor to fit generated notes (a 2-row box can't hold multi-section markdown).
  const editorRows: number = Math.min(14, Math.max(2, description.split("\n").length + 1));

  return (
    <div className={`kuika-releases__card kuika-releases__card--${status}`}>
      <div className={`kuika-releases__card-top ${OVERFLOW_REVEAL_HOST_CLASS}`}>
        <span className="kuika-releases__glyph" aria-hidden="true">
          <KuikaSvg name={status === "released" ? "circle-check" : "rocket"} width={15} height={15} />
        </span>
        {canManage ? (
          <KuikaInput
            size="sm"
            className="kuika-releases__name-input"
            value={name}
            onChange={(_event, value: string | number) => setName(String(value))}
            onBlur={commitName}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
          />
        ) : (
          <span className="kuika-releases__name">
            <KuikaOverflowText>{version.name}</KuikaOverflowText>
          </span>
        )}
        <span className={`kuika-releases__badge kuika-releases__badge--${status}`}>{STATUS_LABEL[status]}</span>
        <span className="kuika-releases__spacer" />
        {canManage && (
          <div className="kuika-releases__actions">
            <BoardDatePicker value={version.releaseDate} onChange={setReleaseDate} placeholder="Set date" />
            <KuikaButton type="secondary" size="sm" onClick={toggleReleased}>
              {version.released ? "Unrelease" : "Release"}
            </KuikaButton>
            <KuikaTooltip content="Delete release" position="bottom">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                isIcon
                aria-label={`Delete release ${version.name}`}
                onClick={() => onRequestDelete(version)}
              >
                <KuikaSvg name="trash" width={14} height={14} />
              </KuikaButton>
            </KuikaTooltip>
          </div>
        )}
      </div>

      <div className="kuika-releases__meta">
        <span className="kuika-releases__date">
          <KuikaSvg name="calendar-event" width={12} height={12} />
          {dateLine}
        </span>
        {progress.total > 0 ? (
          <span className="kuika-releases__progress">
            <span className="kuika-releases__bar">
              <span className="kuika-releases__bar-fill" style={{ width: `${pct}%` }} />
            </span>
            <span className="kuika-releases__progress-text">
              {progress.done} of {progress.total} {progress.total === 1 ? "issue" : "issues"} done
            </span>
          </span>
        ) : (
          <span className="kuika-releases__progress-text kuika-releases__progress-text--muted">No issues pinned</span>
        )}
      </div>

      {canManage ? (
        <div className="kuika-releases__notes">
          <div className="kuika-releases__notes-head">
            <span className="kuika-releases__notes-label">Release notes</span>
            {aiEnabled && (
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                loading={generating}
                onClick={() => void generateNotes()}
              >
                <AiSparkleIcon size={13} />
                <span style={{ marginLeft: 5 }}>{generating ? "Generating…" : "Generate with AI"}</span>
              </KuikaButton>
            )}
          </div>
          <KuikaInput
            type="textarea"
            rows={editorRows}
            resizable={false}
            className="kuika-releases__description-input"
            value={description}
            placeholder="Add release notes…"
            onChange={(_event, value: string | number) => setDescription(String(value))}
            onBlur={commitDescription}
          />
        </div>
      ) : (
        version.description && (
          <div className="kuika-releases__description">
            <MarkedReact gfm breaks value={version.description} renderer={SAFE_MARKDOWN_RENDERER} />
          </div>
        )
      )}
    </div>
  );
};

// The project's releases (fix versions). Issues pin one in their detail rail; here an owner creates
// them, sets a release date, marks them released, or deletes them. Releases are grouped into
// Unreleased (overdue first) and Released, each card showing how many of its pinned issues are done.
const ProjectReleases: React.FC<ProjectReleasesProps> = ({ projectKey }) => {
  const { config, loaded } = useBoardConfig();
  const queryClient = useQueryClient();
  const canManage: boolean = config.canManage;
  const aiEnabled: boolean = useBoardAiEnabled();
  const [newName, setNewName] = useState<string>("");
  const [adding, setAdding] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<Version | null>(null);

  const versions: Version[] = useMemo(
    () => config.versions.filter((version: Version) => version.projectKey === projectKey),
    [config.versions, projectKey]
  );

  // Per-release issue progress (done / total). Refetched on mount and whenever an event that can
  // change the counts fires: a release added/removed ("config.changed") OR an issue-level change —
  // pinning/unpinning a fix version, moving an issue to/from done, archive/restore/delete. Those are
  // issue events, never "config.changed", so we listen on the full stream with the same predicate
  // the roadmap uses (which skips the redundant optimistic echo).
  const progressQuery = useQuery({
    queryKey: boardKeys.versionProgress(projectKey),
    queryFn: (): Promise<VersionProgress[]> => BoardApi.getVersionProgress(projectKey)
  });
  useEffect(() => {
    return BoardService.subscribe((event: BoardChangeEvent) => {
      if (event.kind === "config.changed" || roadmapChanged(event)) {
        void queryClient.invalidateQueries({ queryKey: boardKeys.versionProgress(projectKey) });
      }
    });
  }, [queryClient, projectKey]);

  const progressById: Map<string, ReleaseProgress> = useMemo(() => {
    const map: Map<string, ReleaseProgress> = new Map<string, ReleaseProgress>();
    for (const row of progressQuery.data ?? []) map.set(row.versionId, { total: row.total, done: row.done });
    return map;
  }, [progressQuery.data]);

  const byDate = (a: Version, b: Version, direction: number): number => {
    if (a.releaseDate === b.releaseDate) return 0;
    if (a.releaseDate === null) return 1; // undated sinks to the bottom of either group
    if (b.releaseDate === null) return -1;
    return a.releaseDate < b.releaseDate ? -direction : direction;
  };
  const unreleased: Version[] = useMemo(
    () => versions.filter((v: Version) => !v.released).sort((a: Version, b: Version) => byDate(a, b, 1)),
    [versions]
  );
  const released: Version[] = useMemo(
    () => versions.filter((v: Version) => v.released).sort((a: Version, b: Version) => byDate(a, b, -1)),
    [versions]
  );

  // Optimistically edit the shared config cache's version list so a create/delete shows instantly,
  // instead of waiting on the full /config refetch BoardService fires afterwards (which reconciles).
  const patchCachedVersions = (mutate: (versions: Version[]) => Version[]): void => {
    queryClient.setQueryData<BoardConfig>(boardKeys.config(activeBoardScope()), (old: BoardConfig | undefined) =>
      old ? { ...old, versions: mutate(old.versions) } : old
    );
  };

  const addVersion = async (): Promise<void> => {
    const trimmed: string = newName.trim();
    if (!trimmed || adding) return;
    setAdding(true);
    try {
      const created: Version | null = await BoardService.createVersion(projectKey, trimmed);
      if (created) {
        setNewName(""); // clear only on success — a rejected add keeps the typed name
        patchCachedVersions((vs: Version[]) => (vs.some((v: Version) => v.id === created.id) ? vs : [...vs, created]));
      }
    } finally {
      setAdding(false);
    }
  };

  const renderGroup = (label: string, list: Version[]): React.ReactNode => {
    if (list.length === 0) return null;
    return (
      <section className="kuika-releases__group">
        <div className="kuika-releases__group-head">
          <span className="kuika-releases__group-label">{label}</span>
          <span className="kuika-releases__group-count">{list.length}</span>
        </div>
        <div className="kuika-releases__group-list">
          {list.map((version: Version) => (
            <ReleaseRow
              key={version.id}
              version={version}
              progress={progressById.get(version.id) ?? NO_PROGRESS}
              canManage={canManage}
              aiEnabled={aiEnabled}
              projectKey={projectKey}
              onRequestDelete={setPendingDelete}
              onOptimisticUpdate={(next: Version) =>
                patchCachedVersions((vs: Version[]) => vs.map((v: Version) => (v.id === next.id ? next : v)))
              }
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="kuika-releases">
      <header className="kuika-releases__head">
        <div className="kuika-releases__head-text">
          <div className="kuika-releases__head-title">Releases</div>
          <div className="kuika-releases__head-sub">
            Versions issues can be assigned to as their fix version. Mark one released when it ships.
          </div>
        </div>
      </header>

      {canManage ? (
        <div className="kuika-releases__add">
          <KuikaInput
            size="sm"
            className="kuika-releases__add-input"
            value={newName}
            placeholder="New release name… (e.g. 1.2.0)"
            onChange={(_event, value: string | number) => setNewName(String(value))}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (event.key === "Enter") void addVersion();
            }}
          />
          <KuikaButton
            type="primary"
            size="sm"
            loading={adding}
            disabled={!newName.trim()}
            onClick={() => void addVersion()}
          >
            <KuikaSvg name="plus" width={13} height={13} />
            <span style={{ marginLeft: 5 }}>New release</span>
          </KuikaButton>
        </div>
      ) : (
        <div className="kuika-releases__readonly-note">
          <KuikaSvg name="lock" width={13} height={13} />
          Only workspace owners or supervisors can create or edit releases.
        </div>
      )}

      {loaded && versions.length === 0 ? (
        <div className="kuika-releases__empty">
          <span className="kuika-releases__empty-glyph" aria-hidden="true">
            <KuikaSvg name="rocket" width={22} height={22} />
          </span>
          <div className="kuika-releases__empty-title">No releases yet</div>
          <div className="kuika-releases__empty-text">
            {canManage
              ? "Create a release above to track which version your issues ship in."
              : "No releases have been created for this project yet."}
          </div>
        </div>
      ) : (
        <div className="kuika-releases__groups">
          {renderGroup("Unreleased", unreleased)}
          {renderGroup("Released", released)}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete release?"
        message={`"${pendingDelete?.name ?? ""}" will be removed and unpinned from any issue that fixed it.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        danger
        onConfirm={() => {
          if (pendingDelete) {
            const removedId: string = pendingDelete.id;
            void BoardService.deleteVersion(removedId);
            patchCachedVersions((vs: Version[]) => vs.filter((v: Version) => v.id !== removedId));
          }
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default ProjectReleases;
